// Pointer/drag state machine for the selection sphere + tooltip dispatch.
// Subscribes to renderer.domElement pointer events and dispatches MOVE_SELECTION.
// The drag solve is view-angle gated (see marker.ts) into four modes: 'XY' (the
// horizontal y=0 plane), 'Z' (the world-Y vertical line), 'HAXIS' (one cross-
// screen horizontal world axis, equatorial), and 'VPLANE' (the screen-facing
// vertical plane, equatorial body drag). Hover drives UI_HOVER_SHOW/HIDE.

import * as THREE from 'three';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { SceneCamera } from '@app/context';
import { store } from '@store/instance';
import { INTENTS } from '@store/intents';
import { state, tooltipScratch } from './state';
import {
    setMarkerHover, triggerMarkerPulse, setMarkerActive,
    setHandleHover, setHandleActive, getHandleWorldPosition,
    setHHandleHover, setHHandleActive, getHorizontalHandleWorldPosition,
    cameraElevationDeg, verticalDragAllowed, horizontalDragAxis,
    VERTICAL_MAX_ELEV_DEG, HORIZONTAL_MIN_ELEV_DEG,
} from './marker';

type ThreeNamespace = typeof import('three');

/** Screen-space distance (px) from point (px,py) to the segment (ax,ay)→(bx,by).
 *  Used to make the whole vertical stem grabbable, not just the knob disc. */
function segDistPx(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
    const abx = bx - ax;
    const aby = by - ay;
    const len2 = abx * abx + aby * aby;
    let t = len2 > 0 ? ((px - ax) * abx + (py - ay) * aby) / len2 : 0;
    if (t < 0) t = 0; else if (t > 1) t = 1;
    return Math.hypot(px - (ax + t * abx), py - (ay + t * aby));
}

/** Project the marker's world position to viewport pixels, or null if the
 *  marker/renderer isn't ready. Reuses the tooltip scratch vecs. */
function projectCubeScreen(camera: SceneCamera, renderer: THREE.WebGLRenderer): { screenX: number; screenY: number } | null {
    if (!renderer || !renderer.domElement || !state.cubeCenterDot) return null;
    const rect = renderer.domElement.getBoundingClientRect();
    const t = tooltipScratch();
    state.cubeCenterDot.getWorldPosition(t.world);
    t.ndc.copy(t.world).project(camera);
    return {
        screenX: rect.left + (t.ndc.x * 0.5 + 0.5) * rect.width,
        screenY: rect.top + (-t.ndc.y * 0.5 + 0.5) * rect.height,
    };
}

function showTooltipForCube(camera: SceneCamera, renderer: THREE.WebGLRenderer): void {
    if (!renderer || !renderer.domElement || !state.cubeCenterDot || !state.threeJSToPhysical || !state.getFieldValue) return;
    const phys = state.threeJSToPhysical(state.cubeCenterDot.position.x, state.cubeCenterDot.position.y, state.cubeCenterDot.position.z);
    let value = 0;
    try { value = state.getFieldValue(phys.x, phys.y, phys.z); } catch { value = 0; }
    const coordUnits = (typeof state.getCoordUnitsFn === 'function') ? state.getCoordUnitsFn() : (state.UNITS || '');
    const fieldUnits = (typeof state.getFieldUnitsFn === 'function') ? state.getFieldUnitsFn() : ((state.fieldMetadata && state.fieldMetadata.field_units) ? state.fieldMetadata.field_units : '');
    const fieldLabel = (typeof state.getFieldLabelFn === 'function') ? state.getFieldLabelFn() : 'Field';

    const scr = projectCubeScreen(camera, renderer);
    if (!scr) return;
    const { screenX, screenY } = scr;

    store.dispatch(INTENTS.UI_HOVER_SHOW, {
        screenX, screenY,
        pinned: tooltipPinned,
        sample: {
            posPhys: { x: phys.x, y: phys.y, z: phys.z },
            value,
            fieldLabel,
            coordUnits,
            fieldUnits,
        },
    });
}

function hideTooltip(): void {
    store.dispatch(INTENTS.UI_HOVER_HIDE);
}

// Pin state for the small value/position panel near the selection. A tap on the
// marker toggles it; while pinned, the render loop calls tickSelectionTooltip
// each frame to keep the panel glued to the (re-projected) marker, even as the
// camera orbits. Module-level: one selection marker per session.
let tooltipPinned = false;

/** Per-frame: keep the value/position panel glued to the re-projected marker
 *  as the camera orbits. When pinned, re-assert fully (also refreshes the
 *  sampled value + cancels any pending hide). When not pinned, reposition only
 *  via UI_HOVER_REFLOW — a no-op if the panel is hidden, and it leaves a
 *  transient/fading panel's hide lifecycle untouched so it still dismisses. */
export function tickSelectionTooltip(camera: SceneCamera, renderer: THREE.WebGLRenderer): void {
    if (tooltipPinned) { showTooltipForCube(camera, renderer); return; }
    const scr = projectCubeScreen(camera, renderer);
    if (scr) store.dispatch(INTENTS.UI_HOVER_REFLOW, { screenX: scr.screenX, screenY: scr.screenY });
}

export interface InitInteractionsCtx {
    THREE: ThreeNamespace;
    renderer: THREE.WebGLRenderer;
    camera: SceneCamera;
    controls: OrbitControls | null;
    cubeCenterDot: THREE.Mesh;
    invisibleCubeMesh: THREE.Mesh;
    physicalToThreeJS: (x: number, y: number, z: number) => THREE.Vector3;
    threeJSToPhysical: (x: number, y: number, z: number) => THREE.Vector3;
    getCoordUnits?: () => string;
    getFieldUnits?: () => string;
    getFieldLabel?: () => string;
}

export function initSelectionInteractions(ctx: InitInteractionsCtx): () => void {
    const {
        THREE, renderer, camera, controls,
        cubeCenterDot, invisibleCubeMesh,
        physicalToThreeJS, threeJSToPhysical,
        getCoordUnits, getFieldUnits, getFieldLabel,
    } = ctx;

    if (!renderer || !renderer.domElement || !camera || !THREE || !cubeCenterDot || !invisibleCubeMesh) {
        return () => {};
    }
    state.getCoordUnitsFn = (typeof getCoordUnits === 'function') ? getCoordUnits : null;
    state.getFieldUnitsFn = (typeof getFieldUnits === 'function') ? getFieldUnits : null;
    state.getFieldLabelFn = (typeof getFieldLabel === 'function') ? getFieldLabel : null;
    tooltipPinned = false;

    let isHoveringSelection = false;
    let isHoveringHandle = false;   // over the ↕ knob/stem
    let isHoveringHHandle = false;  // over the ↔ knob/stem
    let isDraggingSelection = false;
    // True while the active drag was initiated on a stem handle (↕ or ↔): the
    // mode is then pinned and Shift is ignored — the touch-friendly path to the
    // same axis-locked motion. Body grabs leave both false (Shift can still flip).
    let dragViaHandle = false;   // grabbed the ↕ knob → 'Z'
    let dragViaHHandle = false;  // grabbed the ↔ stem → 'HAXIS'
    // Which axes the view angle permits, captured at pointerdown (the camera is
    // static during a selection drag — OrbitControls is disabled). Clamps the
    // per-move mode so a gesture can't flip into a degenerate axis.
    let dragVerticalOk = true;
    // The cross-screen horizontal world axis chosen at grab time, or null outside
    // the equatorial regime. Pins HAXIS/VPLANE solves for the whole gesture.
    let dragHorizAxis: 'x' | 'z' | null = null;
    // Single-axis grab offset: the closest-point param along the active axis at
    // grab time. The knob sits off the marker center, so without subtracting this
    // the first move would snap the marker to the cursor. We move only by the
    // *change* in the param (delta drag), like the XY plane. Shared by 'Z'/'HAXIS'.
    let dragAxisGrabOffset = 0;
    const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const dragOffset = new THREE.Vector3();
    const dragRaycaster = new THREE.Raycaster();
    const dragMouseNdc = new THREE.Vector2();
    let dragPointerId: number | null = null;
    // Tap detection: a pointerdown→up on the cube with negligible travel is a
    // click (toggle the Inspector), not a drag (which streams MOVE_SELECTION).
    let downClientX = 0;
    let downClientY = 0;
    const TAP_MAX_PX = 4;
    // Double-tap on the selection recenters the camera on it. Two taps within
    // this window (both small-travel, both on the selection — the pointerup tap
    // branch only runs for presses that started a selection drag) count.
    const DOUBLE_TAP_MS = 320;
    let lastTapMs = 0;
    // Physical coords held fixed for the active mode (the moving coords come from
    // the solve): 'XY' fixes Z; 'VPLANE' fixes one horizontal; 'Z'/'HAXIS' fix the
    // two non-moving coords (read from the drag origin at solve time).
    let dragFixedPhysX = 0;
    let dragFixedPhysY = 0;
    let dragFixedPhysZ = 0;
    let dragMode: 'XY' | 'Z' | 'HAXIS' | 'VPLANE' = 'XY';
    // The body grab's base mode (no Shift): Shift toggles to 'Z' and back to this.
    // Unused for stem grabs (those pin dragMode and ignore Shift).
    let dragBodyMode: 'XY' | 'Z' | 'VPLANE' = 'XY';
    const dragOriginThree = new THREE.Vector3();
    const dragAxisDir = new THREE.Vector3(0, 1, 0); // active single-axis dir (set per grab)
    const DIR_X = new THREE.Vector3(1, 0, 0);
    const DIR_Y = new THREE.Vector3(0, 1, 0);
    const DIR_Z = new THREE.Vector3(0, 0, 1);
    const PLANE_NORMAL_Y = new THREE.Vector3(0, 1, 0);
    const PLANE_NORMAL_X = new THREE.Vector3(1, 0, 0);
    const PLANE_NORMAL_Z = new THREE.Vector3(0, 0, 1);
    const sWorldCenter = new THREE.Vector3();
    const sNdcCenter = new THREE.Vector3();
    const sEdgeWorld = new THREE.Vector3();
    const sNdcEdge = new THREE.Vector3();
    const sRight = new THREE.Vector3();
    const sHitThree = new THREE.Vector3();
    const sRayO = new THREE.Vector3();
    const sRayD = new THREE.Vector3();
    const sW0 = new THREE.Vector3();
    const sNewThree = new THREE.Vector3();
    const sHandleWorld = new THREE.Vector3();
    const sHandleNdc = new THREE.Vector3();
    const sHHandleWorld = new THREE.Vector3();
    const sHHandleNdc = new THREE.Vector3();

    interface ProjectedScreen { centerPxX: number; centerPxY: number; radiusPx: number }
    function projectedCubeScreen(rect: DOMRect): ProjectedScreen {
        cubeCenterDot.getWorldPosition(sWorldCenter);
        sNdcCenter.copy(sWorldCenter).project(camera);
        const centerPxX = (sNdcCenter.x * 0.5 + 0.5) * rect.width;
        const centerPxY = (-sNdcCenter.y * 0.5 + 0.5) * rect.height;
        sRight.set(1, 0, 0).applyQuaternion(camera.quaternion).normalize();
        const geom = cubeCenterDot.geometry;
        const baseRadius = (geom && geom.boundingSphere) ? geom.boundingSphere.radius : 0.08;
        const radiusWorld = baseRadius * (cubeCenterDot.scale ? cubeCenterDot.scale.x : 1);
        sEdgeWorld.copy(sWorldCenter).addScaledVector(sRight, radiusWorld);
        sNdcEdge.copy(sEdgeWorld).project(camera);
        const dxPx = (sNdcEdge.x - sNdcCenter.x) * 0.5 * rect.width;
        const dyPx = (sNdcEdge.y - sNdcCenter.y) * -0.5 * rect.height;
        const radiusPx = Math.sqrt(dxPx * dxPx + dyPx * dyPx);
        return { centerPxX, centerPxY, radiusPx };
    }

    // Screen position of the ↕ drag knob, or null when no handle exists.
    // Reuses scratch vecs (no per-event allocation), mirroring projectedCubeScreen.
    function projectedHandleScreen(rect: DOMRect): { pxX: number; pxY: number } | null {
        if (!getHandleWorldPosition(sHandleWorld)) return null;
        sHandleNdc.copy(sHandleWorld).project(camera);
        return {
            pxX: (sHandleNdc.x * 0.5 + 0.5) * rect.width,
            pxY: (-sHandleNdc.y * 0.5 + 0.5) * rect.height,
        };
    }

    // Screen position of the ↔ drag knob, or null when no handle exists.
    function projectedHHandleScreen(rect: DOMRect): { pxX: number; pxY: number } | null {
        if (!getHorizontalHandleWorldPosition(sHHandleWorld)) return null;
        sHHandleNdc.copy(sHHandleWorld).project(camera);
        return {
            pxX: (sHHandleNdc.x * 0.5 + 0.5) * rect.width,
            pxY: (-sHHandleNdc.y * 0.5 + 0.5) * rect.height,
        };
    }

    // Signed distance along the unit axis `dir` from `p0` to the point on that
    // line closest to the current drag ray (set up in dragRaycaster). NaN when
    // the ray is nearly parallel to the axis (degenerate — gated out upstream).
    function closestAxisParam(p0: THREE.Vector3, dir: THREE.Vector3): number {
        sRayO.copy(dragRaycaster.ray.origin);
        sRayD.copy(dragRaycaster.ray.direction).normalize();
        sW0.copy(sRayO).sub(p0);
        const b = dir.dot(sRayD);
        const denom = 1 - b * b;
        if (Math.abs(denom) <= 1e-6) return Number.NaN;
        const c1 = dir.dot(sW0);
        const c2 = sRayD.dot(sW0);
        return (c1 - b * c2) / denom;
    }

    // Configure the solve for `mode` around `centerThree` (the marker's current
    // three-space position). Assumes dragRaycaster is already set from the pointer.
    // Plane modes capture the grab offset on the plane; axis modes capture the
    // closest-point param so the first move is a no-op (no snap to the cursor).
    function setupMode(mode: 'XY' | 'Z' | 'HAXIS' | 'VPLANE', centerThree: THREE.Vector3): void {
        dragMode = mode;
        const phys = threeJSToPhysical(centerThree.x, centerThree.y, centerThree.z);
        dragFixedPhysX = phys.x;
        dragFixedPhysY = phys.y;
        dragFixedPhysZ = phys.z;
        if (mode === 'XY' || mode === 'VPLANE') {
            const normal = mode === 'XY'
                ? PLANE_NORMAL_Y
                : (dragHorizAxis === 'x' ? PLANE_NORMAL_Z : PLANE_NORMAL_X);
            dragPlane.setFromNormalAndCoplanarPoint(normal, centerThree);
            if (dragRaycaster.ray.intersectPlane(dragPlane, sHitThree)) {
                dragOffset.copy(sHitThree.sub(centerThree));
            } else {
                dragOffset.set(0, 0, 0);
            }
        } else {
            dragOriginThree.copy(centerThree);
            dragAxisDir.copy(mode === 'Z' ? DIR_Y : (dragHorizAxis === 'x' ? DIR_X : DIR_Z));
            const s0 = closestAxisParam(dragOriginThree, dragAxisDir);
            dragAxisGrabOffset = Number.isNaN(s0) ? 0 : s0;
        }
    }

    function onPointerMoveHover(ev: PointerEvent): void {
        if (!cubeCenterDot) return;
        const rect = renderer.domElement.getBoundingClientRect();
        const { centerPxX, centerPxY, radiusPx } = projectedCubeScreen(rect);
        const mx = ev.clientX - rect.left;
        const my = ev.clientY - rect.top;
        const distPx = Math.hypot(mx - centerPxX, my - centerPxY);
        const targetPx = Math.max(26, radiusPx * 2.0);
        isHoveringSelection = distPx <= targetPx;
        // The whole stem (center→knob) is grabbable, same target radius as the
        // core. Precedence: selection circle → ↕ stem → ↔ stem.
        const handle = projectedHandleScreen(rect);
        isHoveringHandle = handle !== null
            && !isHoveringSelection
            && verticalDragAllowed(camera)
            && segDistPx(mx, my, centerPxX, centerPxY, handle.pxX, handle.pxY) <= targetPx;
        // ↔ stem is live only in the equatorial regime with a clear cross-screen axis.
        const equatorial = cameraElevationDeg(camera) < HORIZONTAL_MIN_ELEV_DEG;
        const hAxis = equatorial ? horizontalDragAxis(camera) : null;
        const hHandle = hAxis !== null ? projectedHHandleScreen(rect) : null;
        isHoveringHHandle = hHandle !== null
            && !isHoveringSelection
            && !isHoveringHandle
            && segDistPx(mx, my, centerPxX, centerPxY, hHandle.pxX, hHandle.pxY) <= targetPx;
        setMarkerHover(isHoveringSelection);
        setHandleHover(isHoveringHandle);
        setHHandleHover(isHoveringHHandle);
        const hoverAny = isHoveringSelection || isHoveringHandle || isHoveringHHandle;
        renderer.domElement.style.cursor = hoverAny ? 'pointer' : '';
        // Keep zoom (wheel/pinch) alive over the selection — only rotate/pan are
        // suppressed so a press starts a selection drag, not a camera orbit. The
        // capture-phase onPointerDown stops a grab-target press before
        // OrbitControls sees it, so leaving controls enabled here lets the wheel
        // through while still preventing an accidental orbit.
        if (controls) {
            controls.enabled = !isDraggingSelection;
            controls.enableRotate = !(isDraggingSelection || hoverAny);
            controls.enablePan = !(isDraggingSelection || hoverAny);
        }
        if (hoverAny && !isDraggingSelection) {
            showTooltipForCube(camera, renderer);
        } else if (!isDraggingSelection && !tooltipPinned) {
            hideTooltip();
        }
    }

    // Bound on `document` in the capture phase so it runs *before* OrbitControls'
    // own (target-phase) pointerdown on the canvas. That ordering lets us disable
    // controls before OrbitControls registers the pointer: otherwise, on touch
    // (no hover to pre-disable controls), OrbitControls grabs the press, captures
    // the pointer, and adds a dynamic pointerup listener; we then disable it and
    // stop the event, so its cleanup never runs — the stale pointer breaks the
    // next orbit gesture. We only act on presses that target the canvas.
    function onPointerDown(ev: PointerEvent): void {
        if (typeof ev.isPrimary !== 'undefined' && ev.isPrimary === false) return;
        if (typeof ev.button === 'number' && ev.button !== 0) return;
        if (ev.target !== renderer.domElement) return;
        const rect = renderer.domElement.getBoundingClientRect();
        const mx = ev.clientX - rect.left;
        const my = ev.clientY - rect.top;
        const { centerPxX, centerPxY, radiusPx } = projectedCubeScreen(rect);
        const distPx = Math.hypot(mx - centerPxX, my - centerPxY);
        const targetPx = Math.max(26, radiusPx * 2.0);
        // Which drag axes the current view angle permits (see marker.ts gating).
        const elev = cameraElevationDeg(camera);
        const verticalOk = elev <= VERTICAL_MAX_ELEV_DEG;
        const equatorial = elev < HORIZONTAL_MIN_ELEV_DEG;
        const hAxis = equatorial ? horizontalDragAxis(camera) : null;
        // Precedence: selection circle → ↕ stem → ↔ stem. A faded ↕ knob (vertical
        // off) isn't grabbable; the ↔ stem exists only in the equatorial regime.
        const overCore = isHoveringSelection || distPx <= targetPx;
        const handle = projectedHandleScreen(rect);
        const overHandle = handle !== null
            && verticalOk
            && !overCore
            && segDistPx(mx, my, centerPxX, centerPxY, handle.pxX, handle.pxY) <= targetPx;
        const hHandle = hAxis !== null ? projectedHHandleScreen(rect) : null;
        const overHHandle = hHandle !== null
            && !overCore
            && !overHandle
            && segDistPx(mx, my, centerPxX, centerPxY, hHandle.pxX, hHandle.pxY) <= targetPx;
        if (overCore || overHandle || overHHandle) {
            ev.preventDefault();
            if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation();
            ev.stopPropagation();
            isDraggingSelection = true;
            dragViaHandle = overHandle;
            dragViaHHandle = overHHandle;
            dragVerticalOk = verticalOk;
            dragHorizAxis = hAxis;
            triggerMarkerPulse();
            setMarkerActive(true);
            if (overHandle) setHandleActive(true);
            if (overHHandle) setHHandleActive(true);
            downClientX = ev.clientX;
            downClientY = ev.clientY;
            dragPointerId = ev.pointerId !== undefined ? ev.pointerId : null;
            try { if (dragPointerId !== null) renderer.domElement.setPointerCapture(dragPointerId); } catch { /* swallow */ }
            if (controls) controls.enabled = false;
            const phys = threeJSToPhysical(cubeCenterDot.position.x, cubeCenterDot.position.y, cubeCenterDot.position.z);
            const centerThree = physicalToThreeJS(phys.x, phys.y, phys.z);
            dragMouseNdc.set(((ev.clientX - rect.left) / rect.width) * 2 - 1, -((ev.clientY - rect.top) / rect.height) * 2 + 1);
            dragRaycaster.setFromCamera(dragMouseNdc, camera);
            // Body base mode: equatorial → screen-facing vertical plane (or pure
            // vertical in the ambiguous diagonal); otherwise the free X/Y plane.
            dragBodyMode = equatorial ? (hAxis ? 'VPLANE' : 'Z') : 'XY';
            // ↕ stem → vertical; ↔ stem → its one horizontal axis; body → base
            // mode, with Shift forcing vertical where it's usable.
            let mode: 'XY' | 'Z' | 'HAXIS' | 'VPLANE';
            if (overHandle) mode = 'Z';
            else if (overHHandle) mode = 'HAXIS';
            else mode = (ev.shiftKey === true && verticalOk) ? 'Z' : dragBodyMode;
            if (mode === 'Z' && !verticalOk) mode = 'XY'; // top-down body+Shift guard
            setupMode(mode, centerThree);
        }
    }

    function onPointerDrag(ev: PointerEvent): void {
        if (!isDraggingSelection) return;
        ev.preventDefault();
        if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation();
        ev.stopPropagation();
        const rect = renderer.domElement.getBoundingClientRect();
        dragMouseNdc.set(((ev.clientX - rect.left) / rect.width) * 2 - 1, -((ev.clientY - rect.top) / rect.height) * 2 + 1);
        dragRaycaster.setFromCamera(dragMouseNdc, camera);
        // A stem grab (↕ or ↔) pins its axis and ignores Shift. A body grab lets
        // Shift force vertical (where usable) and reverts to the body's base mode
        // on release. The camera is static during a drag, so the captured axis /
        // plane normal stay valid; only the grab offset is re-captured on a switch.
        const locked = dragViaHandle || dragViaHHandle;
        const desiredMode: 'XY' | 'Z' | 'HAXIS' | 'VPLANE' = locked
            ? dragMode
            : ((ev.shiftKey && dragVerticalOk) ? 'Z' : dragBodyMode);
        if (desiredMode !== dragMode) {
            setupMode(desiredMode, invisibleCubeMesh.position);
        }
        // dragMode is authoritative after the toggle above; branch on it directly.
        if (dragMode === 'XY' || dragMode === 'VPLANE') {
            if (dragRaycaster.ray.intersectPlane(dragPlane, sHitThree)) {
                sHitThree.sub(dragOffset);
                const physHit = threeJSToPhysical(sHitThree.x, sHitThree.y, sHitThree.z);
                if (dragMode === 'XY') {
                    store.dispatch(INTENTS.MOVE_SELECTION, { x: physHit.x, y: physHit.y, z: dragFixedPhysZ });
                } else if (dragHorizAxis === 'x') {
                    // Vertical plane spanning up + world-X (= physical Y); world-Z (physical X) fixed.
                    store.dispatch(INTENTS.MOVE_SELECTION, { x: dragFixedPhysX, y: physHit.y, z: physHit.z });
                } else {
                    // Vertical plane spanning up + world-Z (= physical X); world-X (physical Y) fixed.
                    store.dispatch(INTENTS.MOVE_SELECTION, { x: physHit.x, y: dragFixedPhysY, z: physHit.z });
                }
            }
        } else {
            // 'Z' or 'HAXIS' — single-axis closest-point, moved only by the change
            // in the param since grab (1:1 with the stem, no initial jump).
            const s = closestAxisParam(dragOriginThree, dragAxisDir);
            if (!Number.isNaN(s)) {
                sNewThree.copy(dragOriginThree).addScaledVector(dragAxisDir, s - dragAxisGrabOffset);
                const phys = threeJSToPhysical(sNewThree.x, sNewThree.y, sNewThree.z);
                const originPhys = threeJSToPhysical(dragOriginThree.x, dragOriginThree.y, dragOriginThree.z);
                if (dragMode === 'Z') {
                    store.dispatch(INTENTS.MOVE_SELECTION, { x: originPhys.x, y: originPhys.y, z: phys.z });
                } else if (dragHorizAxis === 'x') {
                    // world-X (= physical Y)
                    store.dispatch(INTENTS.MOVE_SELECTION, { x: originPhys.x, y: phys.y, z: originPhys.z });
                } else {
                    // world-Z (= physical X)
                    store.dispatch(INTENTS.MOVE_SELECTION, { x: phys.x, y: originPhys.y, z: originPhys.z });
                }
            }
        }
        showTooltipForCube(camera, renderer);
    }

    function onPointerUp(ev: PointerEvent): void {
        if (!isDraggingSelection) return;
        if (ev && ev.preventDefault) ev.preventDefault();
        if (ev && typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation();
        if (ev && ev.stopPropagation) ev.stopPropagation();
        isDraggingSelection = false;
        dragViaHandle = false;
        dragViaHHandle = false;
        dragHorizAxis = null;
        setMarkerActive(false);
        setHandleActive(false);
        setHHandleActive(false);
        try { if (dragPointerId !== null) renderer.domElement.releasePointerCapture(dragPointerId); } catch { /* swallow */ }
        dragPointerId = null;
        // Touch/pen have no hover: nothing fires after lift to clear the stale
        // hover flags, so they'd pin controls off and the next gesture couldn't
        // orbit. The finger is gone — every touch is a fresh gesture — so clear
        // hover and hand control straight back. Mouse keeps the hover-aware path:
        // unconditional `true` there let OrbitControls grab the second press of a
        // double-click (still hovering) and start a stuck rotate; onPointerMoveHover
        // re-enables it as soon as the cursor leaves the marker.
        const isTouch = ev.pointerType === 'touch' || ev.pointerType === 'pen';
        if (isTouch) {
            isHoveringSelection = false;
            isHoveringHandle = false;
            isHoveringHHandle = false;
            setMarkerHover(false);
            setHandleHover(false);
            setHHandleHover(false);
        }
        const stillHover = isHoveringSelection || isHoveringHandle || isHoveringHHandle;
        if (controls) {
            controls.enabled = true;
            controls.enableRotate = isTouch || !stillHover;
            controls.enablePan = isTouch || !stillHover;
        }
        // A tap (no meaningful travel) toggles the small value/position panel
        // near the selection; a real drag just ends. Either way, sample at the
        // cube's current position so the panel has fresh data.
        const travel = Math.hypot(ev.clientX - downClientX, ev.clientY - downClientY);
        if (travel <= TAP_MAX_PX) {
            const now = performance.now();
            const isDoubleTap = now - lastTapMs < DOUBLE_TAP_MS;
            // Reset after a double so a third quick tap doesn't chain-fire.
            lastTapMs = isDoubleTap ? 0 : now;
            const phys = threeJSToPhysical(cubeCenterDot.position.x, cubeCenterDot.position.y, cubeCenterDot.position.z);
            if (isDoubleTap) {
                // Recenter the camera on the selection; undo the first tap's pin
                // so a double-click doesn't leave the panel stuck open.
                tooltipPinned = false;
                hideTooltip();
                store.dispatch(INTENTS.CAMERA_FOCUS_POINT, { x: phys.x, y: phys.y, z: phys.z });
                return;
            }
            tooltipPinned = !tooltipPinned;
            store.dispatch(INTENTS.MOVE_SELECTION, { x: phys.x, y: phys.y, z: phys.z });
            if (tooltipPinned) showTooltipForCube(camera, renderer);
            else hideTooltip();
        } else if (!tooltipPinned) {
            hideTooltip();
        }
    }

    function onWheelZoom(): void { hideTooltip(); }

    const canvas = renderer.domElement;
    canvas.style.touchAction = 'none';
    canvas.addEventListener('pointermove', onPointerMoveHover);
    canvas.addEventListener('wheel', onWheelZoom, { passive: true });
    // Capture phase on document → fires before OrbitControls' canvas pointerdown.
    document.addEventListener('pointerdown', onPointerDown, true);
    canvas.addEventListener('pointermove', onPointerDrag);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);

    return function disposeSelectionInteractions(): void {
        try { canvas.removeEventListener('pointermove', onPointerMoveHover); } catch { /* swallow */ }
        try { canvas.removeEventListener('wheel', onWheelZoom); } catch { /* swallow */ }
        try { document.removeEventListener('pointerdown', onPointerDown, true); } catch { /* swallow */ }
        try { canvas.removeEventListener('pointermove', onPointerDrag); } catch { /* swallow */ }
        try { canvas.removeEventListener('pointerup', onPointerUp); } catch { /* swallow */ }
        try { canvas.removeEventListener('pointercancel', onPointerUp); } catch { /* swallow */ }
    };
}
